import { Component, OnInit, OnDestroy } from "@angular/core";
import { ViewType } from "src/app/common/enums/view-type.enum";
import { DropdownComponent } from "../../common/components/core/dropdown/dropdown.component";
import { TileLayoutComponent } from "../../common/components/tile-layout/tile-layout.component";
import { BillingComponent } from "./billing/billing.component";
import { CensusComponent } from "./census/census.component";
import { InternalCommunicationComponent } from "./internal-communication/internal-communication.component";
import { LtcManagementComponent } from "./ltc-management/ltc-management.component";
import { ScheduleComponent } from "./schedule/schedule.component";
import { SubacuteManagementComponent } from "./subacute-management/subacute-management.component";
import { TasksComponent } from "./tasks/tasks.component";
import { KPIComponent } from "./KPI/kpi.component";
import { ViewTypeService } from "src/app/services/view-type.service";
import { Subscription } from "rxjs";
import { CommonModule } from "@angular/common";
import { LandingClientService } from "src/app/common/services/landing-page.client";
import { LandingPageService } from "./services/landing-page.service";
import { SidePanelDetails } from "src/app/common/interfaces/home/side-panel-details.interface";
import { SidePanelComponent } from "./side-panel/side-panel.component";
import { StorageService } from "src/app/common/services/storage.service";
import { PageHeaderComponent } from "../page-header/page-header.component";

@Component({
  selector: "app-landing-page",
  standalone: true,
  imports: [
    CommonModule,
    TileLayoutComponent,
    DropdownComponent,
    KPIComponent,
    SidePanelComponent,
    PageHeaderComponent
  ],
  templateUrl: "./landing-page.component.html",
  styleUrls: ["./landing-page.component.scss"],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  public dropdownOptions = [
    { id: ViewType.DOR_VIEW, name: "DOR View" },
    { id: ViewType.THERAPIST_VIEW, name: "Therapist View" },
  ];

  selectedViewType: ViewType = ViewType.NONE;
  showDropdown: boolean = false;
  selectedViewId = ViewType.THERAPIST_VIEW;

  private viewTypeSubscription!: Subscription;

  dorViewFirstLayoutItems = [
    { title: "KPI", component: KPIComponent, height: "450px" },
    { title: "Tasks", component: TasksComponent, height: "450px" },
    {
      title: "Internal Communication",
      component: InternalCommunicationComponent,
      height: "400px",
    },
  ];

  dorViewSecondLayoutItems = [
    { title: "Census", component: CensusComponent, height: "auto" },
    {
      title: "Subacute Management",
      component: SubacuteManagementComponent,
      height: "auto",
    },
    {
      title: "Long Term Care Management",
      component: LtcManagementComponent,
      height: "auto",
    },
  ];

  therapistViewFirstLayoutItems = [
    { title: "Schedule", component: ScheduleComponent, height: "450px" },
    {
      title: "Internal Communication",
      component: InternalCommunicationComponent,
      height: "400px",
    },
  ];

  therapistViewSecondLayoutItems = [
    { title: "Tasks", component: TasksComponent, height: "450px" },
    {
      title: "Productivity",
      component: KPIComponent,
      height: "410px",
    },
    { title: "Billing", component: BillingComponent, height: "300px" },
  ];

  public firstLayoutItems: any = this.dorViewFirstLayoutItems;
  public secondLayoutItems: any = this.dorViewSecondLayoutItems;

  sidePanelData!: SidePanelDetails;
  sidePanelVisible: boolean = false;
  sidePanelVisibilitySubscription!: Subscription;
  sidePanelDataSubscription!: Subscription;

  constructor(
    private viewTypeService: ViewTypeService,
    private landingClientService: LandingClientService,
    private landingPageService: LandingPageService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.landingClientService
      .getJobTitleDetails()
      .subscribe((userTitleDetails) => {
        const user = JSON.parse(this.storageService.getUser() ?? "");
        if (
          (userTitleDetails && userTitleDetails.isDOR) ||
          user.issuperuser === true
        ) {
          this.showDropdown = true;
          this.selectedViewType = ViewType.DOR_VIEW;
          this.selectedViewId = ViewType.DOR_VIEW;
        } else {
          this.showDropdown = false;
          this.selectedViewType = ViewType.THERAPIST_VIEW;
          this.selectedViewId = ViewType.THERAPIST_VIEW;
        }
        this.updateLayoutItems();
      });

    this.viewTypeSubscription =
      this.viewTypeService.selectedViewType$.subscribe((viewType) => {
        this.selectedViewType = viewType;
        this.updateLayoutItems();
      });

    this.sidePanelDataSubscription =
      this.landingPageService.sidePanelData$.subscribe((data) => {
        this.sidePanelData = data;
      });

    this.sidePanelVisibilitySubscription =
      this.landingPageService.sidePanelVisible$.subscribe((visible) => {
        this.sidePanelVisible = visible;
      });
  }

  closeSidePanel() {
    this.landingPageService.toggleSidePanel(false);
  }

  ngOnDestroy(): void {
    this.viewTypeService.setSelectedViewType(ViewType.NONE); 
    this.landingPageService.toggleSidePanel(false);
    if (this.viewTypeSubscription) {
      this.viewTypeSubscription.unsubscribe();
    }
    if (this.sidePanelDataSubscription) {
      this.sidePanelDataSubscription.unsubscribe();
    }
    if (this.sidePanelVisibilitySubscription) {
      this.sidePanelVisibilitySubscription.unsubscribe();
    }
  }

  async updateLayoutItems(): Promise<void> {
    if(this.selectedViewType === ViewType.NONE){
      this.firstLayoutItems=[];
      this.secondLayoutItems=[];
    } else if (this.selectedViewType === ViewType.DOR_VIEW) {
      this.firstLayoutItems = this.dorViewFirstLayoutItems;
      this.secondLayoutItems = this.dorViewSecondLayoutItems;
    } else if (this.selectedViewType === ViewType.THERAPIST_VIEW) {
      this.firstLayoutItems = this.therapistViewFirstLayoutItems;
      this.secondLayoutItems = this.therapistViewSecondLayoutItems;
    }
  }

  public onViewChange(selectedOption: any): void {
    const viewId = selectedOption.value;
    if (viewId === 1) {
      this.viewTypeService.setSelectedViewType(ViewType.DOR_VIEW);
    } else if (viewId === 2) {
      this.viewTypeService.setSelectedViewType(ViewType.THERAPIST_VIEW);
    }
  }
}
